import { createAction, createSlice, isAnyOf } from "@reduxjs/toolkit";
import _ from "lodash";
import { OpenMap } from "react-arborist/dist/state/open-slice";
import { TProprtiesResponse } from "../../components/resurs-props/field/types";
import { TResursTreeNode } from "../../components/tree/treeTypes";
import { ResursTreeApi } from "../modules/tree";

type TTree = {
  user: ResursTreeApi.TTreeNodeList | [];
  device: ResursTreeApi.TTreeNodeList | [];
  config: ResursTreeApi.TTreeNodeList | [];
  home: ResursTreeApi.TTreeNodeList | [];
  balance: ResursTreeApi.TTreeNodeList | [];
  operator: ResursTreeApi.TTreeNodeList | [];
  planner: ResursTreeApi.TTreeNodeList | [];
  currentProps: TProprtiesResponse | null;
  currentPropsTree: string | null; // Дерево, которому принадлежат currentProps
  canCreateTreeLink: boolean;
  selectedNodes: {
    tree: string | null;
    objectIds: string[];
    nodes: TResursTreeNode[];
  };
  lastTargetDrop: string;
  openState: OpenMap; // Массив ID открытых узлов
};
const initialState: TTree = {
  user: [],
  device: [],
  config: [],
  home: [],
  balance: [],
  operator: [],
  planner: [],
  currentProps: null,
  currentPropsTree: null,
  canCreateTreeLink: false,
  selectedNodes: { tree: "", objectIds: [], nodes: [] },
  openState: {},
  lastTargetDrop: "",
};

export const treeInsNode =
  createAction<ResursTreeApi.TTreeUpdateResponseIns>("tree/node-ins");
export const treeUpdNode =
  createAction<ResursTreeApi.TTreeUpdateResponseUpd>("tree/node-upd");
export const treeDelNode =
  createAction<ResursTreeApi.TTreeUpdateResponseDel>("tree/node-del");

export const treeInsNodeBatch = createAction<
  ResursTreeApi.TTreeUpdateResponseIns[]
>("tree/node-ins-batch");
export const treeUpdNodeBatch = createAction<
  ResursTreeApi.TTreeUpdateResponseUpd[]
>("tree/node-upd-batch");
export const treeDelNodeBatch = createAction<
  ResursTreeApi.TTreeUpdateResponseDel[]
>("tree/node-del-batch");

export const clearSelectNodes = createAction("tree/clear-select-nodes");
export const clearCurrentProps = createAction("tree/clearCurrentProps");

export const toInitialState = createAction("tree/init");

export const setLastTargetDrop = createAction<string>("tree/last-target-drop");

export const selectNodes = createAction<{
  tree: string | null;
  objectIds: string[];
  nodes: TResursTreeNode[];
}>("tree/select-nodes");

const updateBigArrayFields = (
  bigArray: ResursTreeApi.TTreeNodeList,
  updArray: ResursTreeApi.TTreeUpdateResponseUpd
) => {
  return bigArray.map((obj) => {
    const upd = updArray.upd.find((item) => item.id === obj.id);
    if (upd) {
      return _.merge({}, obj, upd);
    }

    return obj;
  });
};

const removeItemsByIds = (
  bigArray: ResursTreeApi.TTreeNodeList,
  idsToRemove: any
) => {
  return bigArray.filter((item) => !idsToRemove.includes(item.id));
};

const treeSlice = createSlice({
  name: "tree",
  initialState,
  reducers: {
    openNode: (state, action) => {
      const { tree, nodeId } = action.payload;
      const key = `${tree}_${nodeId}`;
      state.openState[key] = true;
      localStorage.setItem("openMap", JSON.stringify(state.openState));
    },
    closeNode: (state, action) => {
      const { tree, nodeId } = action.payload;
      const key = `${tree}_${nodeId}`;
      state.openState[key] = false;
      localStorage.setItem("openMap", JSON.stringify(state.openState));
    },
    toggleNode: (state, action) => {
      const { tree, nodeId } = action.payload;
      const key = `${tree}_${nodeId}`;
      const currentState = !!state.openState[key];
      state.openState[key] = !currentState;
      localStorage.setItem("openMap", JSON.stringify(state.openState));
    },
    setOpenMap: (state, action) => {
      state.openState = action.payload;
      localStorage.setItem("openMap", JSON.stringify(state.openState));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setLastTargetDrop, (state, { payload }) => {
        state.lastTargetDrop = payload;
      })
      .addCase(toInitialState, () => initialState)
      .addCase(treeUpdNode, (state, { payload }) => {
        state[payload.tree] = updateBigArrayFields(
          state[payload.tree],
          payload
        );
      })
      .addCase(treeInsNode, (state, { payload }) => {
        state[payload.tree] = Array.prototype.concat(
          state[payload.tree],
          payload.ins
        );
      })
      .addCase(treeDelNode, (state, { payload }) => {
        state[payload.tree] = removeItemsByIds(
          state[payload.tree],
          payload.del
        );
      })
      .addCase(treeInsNodeBatch, (state, action) => {
        action.payload.forEach((ins) => {
          state[ins.tree] = Array.prototype.concat(state[ins.tree], ins.ins);
        });
      })
      .addCase(treeUpdNodeBatch, (state, action) => {
        action.payload.forEach((upd) => {
          state[upd.tree] = updateBigArrayFields(state[upd.tree], upd);
        });
      })
      .addCase(treeDelNodeBatch, (state, action) => {
        action.payload.forEach((del) => {
          state[del.tree] = removeItemsByIds(state[del.tree], del.del);
        });
      })
      .addCase(
        ResursTreeApi.canCreateTreeLink.fulfilled,
        (state, { payload }) => {
          state.canCreateTreeLink = payload;
        }
      )
      .addCase(ResursTreeApi.getPropertysRedux.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.currentProps = action.payload;
        state.currentPropsTree = action.meta.arg.tree;
      })
      .addCase(clearCurrentProps, (state) => {
        state.currentProps = null;
        state.currentPropsTree = null;
      })
      .addCase(selectNodes, (state, { payload }) => {
        state.selectedNodes = payload;
      })
      .addCase(clearSelectNodes, (state) => {
        state.selectedNodes = { tree: "", objectIds: [], nodes: [] };
      })
      .addMatcher(
        isAnyOf(
          ResursTreeApi.getTree("user").fulfilled,
          ResursTreeApi.getTree("device").fulfilled,
          ResursTreeApi.getTree("config").fulfilled,
          ResursTreeApi.getTree("home").fulfilled,
          ResursTreeApi.getTree("balance").fulfilled,
          ResursTreeApi.getTree("operator").fulfilled,
          ResursTreeApi.getTree("planner").fulfilled
        ),
        (state, { payload }) => {
          if (payload && payload.data && payload.data.ins) {
            // Ваш код здесь, который использует payload.data.ins
            if (payload.tree === "home") {
              state[payload.tree] = payload.data.ins;
            } else {
              state[payload.tree] = _.unionBy(
                Array.prototype.concat(state[payload.tree], payload.data.ins),
                "id"
              );
            }
          }
        }
      );
  },
});

export const { openNode, closeNode, toggleNode, setOpenMap } =
  treeSlice.actions;
export default treeSlice.reducer;
